/* eslint-disable no-console */
import { IMedia } from '@rendora/common/entities/upload_media_v3';
import { getApiClient } from '@rendora/common/services/api';
import { UPLOAD_STATUS } from '@rendora/common/types/enums';
import ajax from './ajax';
import type {
  IAuth,
  IBizParams,
  IDataSource,
  IExtras,
  IPrepareSuccess,
  IServiceOptions,
  ISuccess,
  TOnFail,
  TOnProgress,
  TOnSuccess,
} from './types';

// 上传错误类型定义
interface IUploadError {
  code?: string;
  message: string;
  upload_secret_id?: string;
  info?: unknown;
}

export default class XMinIOUpload {
  private TAG = '[XMinIOUpload]';
  private auth!: IAuth;
  dataSource!: IDataSource;
  file!: File;
  private interval = 3000; // 状态轮询间隔，3 秒
  private media: IMedia = {} as unknown as IMedia;
  private params!: IBizParams;
  private progress = {
    current: 0,
    delta: 0,
  };

  private ignore_query_status = false;
  private is_transcoding = false; // 默认资源不转码
  private timer = 0;
  uid = '';
  upload_secret_id = '';
  private upload_path = '';
  private callback_url = ''; // 从 prepare 接口返回的 callback_url
  private preUrl = ''; // 用于检测页面跳转

  private onProgress: TOnProgress = (percent: number, extras: IExtras) => {
    console.log(this.TAG, percent, extras);
  };

  onMinIOSuccess?: () => void;

  private onSuccess: TOnSuccess = (media: ISuccess) => {
    console.log(this.TAG, '上传完成：', media);
  };

  private onFail: TOnFail = (msg: string, extras: IExtras) => {
    console.log(this.TAG, msg, extras);
  };

  constructor(options: IServiceOptions) {
    const {
      auth,
      dataSource,
      is_transcoding,
      params,
      ignore_query_status,
      onProgress,
      onOssSuccess,
      onSuccess,
      onFail,
    } = options;
    if (!options.auth) {
      console.error(this.TAG, '缺少初始化信息');
      return;
    }
    if (!options.dataSource) {
      console.error(this.TAG, '初始化需要：实现 dataSource');
      return;
    }
    this.auth = auth;
    this.dataSource = dataSource;
    if (ignore_query_status !== undefined) {
      this.ignore_query_status = ignore_query_status;
    }
    if (is_transcoding !== undefined) {
      this.is_transcoding = is_transcoding;
    }
    this.params = params;
    if (onProgress) {
      this.onProgress = onProgress;
    }
    if (onSuccess) {
      this.onSuccess = onSuccess;
    }
    if (onOssSuccess) {
      this.onMinIOSuccess = onOssSuccess;
    }
    if (onFail) {
      this.onFail = onFail;
    }
    console.log(this.TAG, '初始化成功');
  }

  /**
   * 发送回调请求到后端
   */
  private async _postCallback(data: {
    is_transcoding?: boolean;
    upload_secret_id: string;
    upload_path: string;
    real_filename: string;
    resource_type?: number;
    upload_type?: number;
    model_type?: number;
    user_id: string | number;
  }) {
    // 如果没有 callback_url，使用默认的相对路径
    const url = this.callback_url || '/youyan/upload_media_v3/minio_callback/';
    console.log(this.TAG, '发送回调请求', {
      url,
      upload_secret_id: data.upload_secret_id,
      filename: data.real_filename,
    });
    // 否则使用 ApiClient（相对路径）
    const client = await getApiClient();
    const response = await client.post<void>(url, data);
    console.log(this.TAG, '回调请求成功', { upload_secret_id: data.upload_secret_id });
    return response.data;
  }

  /**
   * 上传回调到后端
   */
  private async _uploadCallback() {
    try {
      // 如果没有 callback_url，跳过回调
      if (!this.callback_url) {
        console.warn(this.TAG, 'callback_url 未定义，跳过回调');
        return;
      }

      console.log(this.TAG, '开始上传回调', {
        upload_secret_id: this.upload_secret_id,
        upload_path: this.upload_path,
      });
      const { name: filename } = this.file;
      const { resource_type, upload_type, model_type } = this.params || {};
      const { user_id } = this.auth;
      await this._postCallback({
        is_transcoding: this.is_transcoding,
        upload_secret_id: this.upload_secret_id,
        upload_path: this.upload_path,
        real_filename: filename,
        resource_type,
        upload_type,
        model_type,
        user_id,
      });
      console.log(this.TAG, '上传回调完成', { upload_secret_id: this.upload_secret_id });
    } catch (error) {
      console.error(this.TAG, '回调失败', error);
    }
  }

  private _onProgress(percent: number, extras: IExtras) {
    const intPercent = +percent.toFixed(0);
    const validPercent = intPercent > 100 ? 100 : intPercent;
    this.progress.current = validPercent;
    this.media = extras.media || this.media;
    this.onProgress(validPercent, {
      uid: this.uid,
      file: this.file,
      media: this.media,
      params: this.params,
    });
  }

  private _onFail(msg: string) {
    console.warn(this.TAG, '上传失败：', msg);
    this.onFail(msg, { uid: this.uid, file: this.file, media: this.media });
  }

  private _onSuccess() {
    // 根据参考类型，ISuccess 应该只包含 uid 和 IMedia 的字段
    // 但当前类型定义中包含 upload_path，为保持兼容性保留
    this.onSuccess({
      uid: this.uid,
      upload_path: this.upload_path,
      ...this.media,
      meta: this.params as unknown as Record<string, unknown>,
    });
    console.log(this.TAG, '上传完成：', new Date().toLocaleString());
  }

  /**
   * 检查上传状态（轮询）
   * 根据传入代码逻辑：检查状态并更新进度，完成后停止轮询
   */
  private async _checkStatus() {
    if (
      this.ignore_query_status ||
      (this.preUrl && this.preUrl !== window?.location?.href) ||
      !this.upload_secret_id
    ) {
      if (this.ignore_query_status) {
        console.log(this.TAG, '跳过状态检查（ignore_query_status=true）');
      }
      if (this.preUrl && this.preUrl !== window?.location?.href) {
        console.log(this.TAG, '跳过状态检查（页面已跳转）', {
          preUrl: this.preUrl,
          currentUrl: window?.location?.href,
        });
      }
      if (!this.upload_secret_id) {
        console.log(this.TAG, '跳过状态检查（upload_secret_id 未设置）');
      }
      return;
    }
    try {
      console.log(this.TAG, '开始检查上传状态', { upload_secret_id: this.upload_secret_id });
      const response = await this.dataSource.queryStatus({
        upload_secret_id: this.upload_secret_id,
      });

      // dataSource.queryStatus 返回的是 IUploadStatus，直接使用
      // 传入代码中使用 response?.data，但当前实现直接返回 IUploadStatus
      if (response?.upload_status !== undefined) {
        console.log(this.TAG, '状态检查结果', {
          upload_status: response.upload_status,
          percent: response?.percent,
          upload_secret_id: this.upload_secret_id,
        });
        // 检查状态，如果不是完成或错误，继续轮询
        if (![UPLOAD_STATUS.FINISHED, UPLOAD_STATUS.ERROR].includes(response.upload_status)) {
          // 更新进度
          if (this.upload_secret_id && response?.percent !== undefined) {
            this._onProgress(response.percent, {
              uid: this.uid,
              file: this.file,
              media: response as unknown as IMedia,
            });
          }
          // 继续轮询
          console.log(this.TAG, `继续轮询，${this.interval}ms 后再次检查`, {
            upload_secret_id: this.upload_secret_id,
          });
          this.timer = window.setTimeout(() => {
            this._checkStatus();
          }, this.interval);
        }

        // 上传完成
        if (response.upload_status === UPLOAD_STATUS.FINISHED) {
          console.log(this.TAG, '上传状态：已完成', { upload_secret_id: this.upload_secret_id });
          // 更新 media 信息
          if (response.media) {
            this.media = {
              ...response.media,
              relatedObject: response.related_object,
            } as IMedia;
          } else {
            // 如果没有 media，从 response 中构建
            this.media = {
              ...this.media,
              relatedObject: response.related_object,
            } as IMedia;
          }
          this._onSuccess();
          window.clearTimeout(this.timer);
        }

        // 上传失败
        if (response.upload_status === UPLOAD_STATUS.ERROR) {
          console.error(this.TAG, '上传状态：失败', {
            upload_secret_id: this.upload_secret_id,
            failed_info: response.failed_info,
          });
          this._onFail(response.failed_info?.detail || '上传失败');
          window.clearTimeout(this.timer);
        }
      } else {
        console.warn(this.TAG, '状态检查返回数据异常', {
          response,
          upload_secret_id: this.upload_secret_id,
        });
      }
    } catch (error) {
      console.error(this.TAG, '状态检查失败', { error, upload_secret_id: this.upload_secret_id });
    }
  }

  /**
   * 上传到 MinIO
   */
  private async _uploadMinIO(values: IPrepareSuccess) {
    try {
      const signedUrl = values?.credential?.signed_url || '';
      console.log(this.TAG, '开始上传到 MinIO', {
        uid: this.uid,
        filename: this.file.name,
        filesize: this.file.size,
        upload_secret_id: this.upload_secret_id,
        signed_url: signedUrl ? '已设置' : '未设置',
      });
      await ajax(signedUrl, {
        method: 'PUT',
        data: this.file,
        onSuccess: () => {
          console.log(this.TAG, 'MinIO 上传成功', {
            uid: this.uid,
            upload_secret_id: this.upload_secret_id,
            ignore_query_status: this.ignore_query_status,
          });
          if (this.ignore_query_status) {
            this._onSuccess();
          } else {
            this._uploadCallback();
            this._checkStatus();
          }
        },
        onProgress: (percent: number) => {
          this._onProgress(percent, { uid: this.uid, file: this.file, media: this.media });
        },
      });
    } catch (error) {
      console.error(this.TAG, 'MinIO 上传失败', {
        error,
        uid: this.uid,
        upload_secret_id: this.upload_secret_id,
      });
      this._onFail('MinIO 上传失败');
    }
  }

  async upload(file: File & { uid: string }) {
    try {
      if (!file) {
        console.warn(this.TAG, '上传文件为空');
        return;
      }

      this.ignore_query_status = false;
      this.uid = file.uid || `uid-${+Date.now()}`;
      this.file = file;

      const { name: filename, size: filesize } = this.file;
      console.log(this.TAG, '开始上传', {
        uid: this.uid,
        filename,
        filesize,
        is_transcoding: this.is_transcoding,
        params: this.params,
      });

      const response = await this.dataSource.prepare({
        auth_type: 'minio',
        filename,
        filesize,
        ...this.params,
      });

      console.log(this.TAG, 'prepare 接口调用成功', {
        uid: this.uid,
        upload_path: response?.upload_path,
        callback_url: response?.callback_url,
      });

      // dataSource 实际返回的是 IPrepareSimpleUploadResponse，包含 upload_secret_id
      const responseWithSecretId = response as unknown as { upload_secret_id?: string };
      if (responseWithSecretId?.upload_secret_id) {
        this.upload_secret_id = responseWithSecretId.upload_secret_id;
        console.log(this.TAG, '获取到 upload_secret_id', {
          upload_secret_id: this.upload_secret_id,
        });
      }

      if (response) {
        this.preUrl = window?.location?.href || '';
        this.upload_path = response.upload_path;
        this.callback_url = response.callback_url;
        this._uploadMinIO(response);
      } else {
        console.warn(this.TAG, 'prepare 接口返回为空', { uid: this.uid });
      }
    } catch (error) {
      const err = error as IUploadError;
      if (err?.upload_secret_id) {
        this.upload_secret_id = err.upload_secret_id;
        console.log(this.TAG, '从错误中获取到 upload_secret_id', {
          upload_secret_id: this.upload_secret_id,
        });
      }
      const message = typeof error === 'string' ? error : err?.message || '上传准备失败';
      console.error(this.TAG, '上传准备失败', { error, uid: this.uid, message });
      this._onFail(message);
    }
  }

  async simpleUpload(file: File & { uid: string }) {
    try {
      if (!file) {
        console.warn(this.TAG, '简单上传文件为空');
        return;
      }

      this.ignore_query_status = true;
      this.uid = file.uid || `uid-${+Date.now()}`;
      this.file = file;
      const { name: filename, size: filesize } = this.file;
      console.log(this.TAG, '开始简单上传', {
        uid: this.uid,
        filename,
        filesize,
        params: this.params,
      });

      const response = await this.dataSource.prepareSimpleUpload({
        auth_type: 'minio',
        filename,
        filesize,
        ...this.params,
      });

      console.log(this.TAG, 'prepareSimpleUpload 接口调用成功', {
        uid: this.uid,
        upload_path: response?.upload_path,
        callback_url: response?.callback_url,
      });

      if (response) {
        this.upload_path = response.upload_path;
        this.callback_url = response.callback_url;
        this._uploadMinIO(response);
      } else {
        console.warn(this.TAG, 'prepareSimpleUpload 接口返回为空', { uid: this.uid });
      }
    } catch (error) {
      const err = error as IUploadError;
      const message = typeof error === 'string' ? error : err?.message || '简单上传准备失败';
      console.error(this.TAG, '简单上传准备失败', { error, uid: this.uid, message });
      this._onFail(message);
    }
  }

  // ========== 以下方法在原代码中存在，但传入代码中未实现，保留接口并添加注释 ==========

  /**
   * 取消上传
   * 注意：传入代码中未实现此方法，此处保留接口以维持兼容性
   */
  cancel(options?: { deleteData?: boolean }) {
    // TODO: 传入代码中未实现取消功能，需要根据实际需求补充
    console.log(this.TAG, '取消上传', { uid: this.uid, upload_secret_id: this.upload_secret_id });
    if (this.timer) {
      window.clearTimeout(this.timer);
      this.timer = 0;
    }
    const params: { upload_secret_id: string; delete_data?: boolean } = {
      upload_secret_id: this.upload_secret_id,
    };
    if (options?.deleteData) {
      params.delete_data = true;
    }
    return this.dataSource.cancel(params);
  }

  /**
   * 删除上传文件
   * 注意：传入代码中未实现此方法，此处保留接口以维持兼容性
   */
  remove() {
    // TODO: 传入代码中未实现删除功能，需要根据实际需求补充
    console.log(this.TAG, '删除上传文件', {
      uid: this.uid,
      upload_secret_id: this.upload_secret_id,
    });
    return this.dataSource.remove({ upload_secret_id: this.upload_secret_id });
  }

  /**
   * 资源清理方法
   * 注意：传入代码中未实现此方法，此处保留以维持兼容性
   */
  cleanup(): void {
    console.log(this.TAG, '清理资源', {
      uid: this.uid,
      upload_secret_id: this.upload_secret_id,
      hasTimer: !!this.timer,
    });
    if (this.timer) {
      window.clearTimeout(this.timer);
      this.timer = 0;
    }
  }

  /**
   * 完成上传
   * 注意：传入代码中使用 _onSuccess，此处保留方法名以维持兼容性
   */
  finish(media: IMedia) {
    console.log(this.TAG, '手动完成上传', {
      uid: this.uid,
      upload_secret_id: this.upload_secret_id,
      media,
    });
    this.cleanup();
    this.media = media;
    this._onSuccess();
  }
}
